import { pick, concat, set, findIndex } from 'lodash'
import { http } from 'utils'

// Vetting booking request mutation imports.
import {
  SET_VETTING_REQUESTS, SET_VETTING_PAGINATION, ADD_VETTING_REQUESTS, SET_VETTING_REQUEST,
  RESET_VETTING_REQUEST, SET_VETTING_REQUEST_PROPERTY, REMOVE_VETTING_REQUEST_TALENT,
  SET_FILTER, RESET_FILTERS, SET_VETTING_COUNT
} from 'vx/mutations'

// Vetting booking initial state.
import initialRequestState from 'vx/state/booking'
import initialFiltersState from 'vx/state/booking/vettingFilters'

// Vetting booking request state.
const state = {
  filters: initialFiltersState(),
  request: initialRequestState(),
  requests: [],
  count: 0,
  pagination: {}
}

// Vetting booking request state getters.
const getters = {
  /**
   * Initial vetting booking request filters.
   *
   * @return object
   */
  initialFiltersState () {
    return initialFiltersState()
  },

  /**
   * Determine if we have a vetting booking request.
   *
   * @return bool
   */
  haveVettingBookingRequest: (state) => {
    return state.request !== null && state.request.id !== null
  },

  /**
   * Determine if we have no more vetting booking requests to load.
   *
   * @return bool
   */
  haveNoMoreVettingBookingRequests: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  }
}

// Vetting booking request actions.
const actions = {
  /**
   * Get a single vetting booking request.
   *
   * @return Promise
   */
  getVettingBookingRequest ({ state, commit }, { id }) {
    return http.get(`vetting/${id}`)
      .then((response) => {
        commit(SET_VETTING_REQUEST, response.data)
        if (response.data.recently_read === true) {
          commit(SET_VETTING_COUNT, state.count > 0 ? state.count - 1 : 0)
        }

        return response
      })
  },

  /**
   * Get the vetting booking requests.
   *
   * @return Promise
   */
  getVettingBookingRequests ({ commit }, params = {}) {
    return http.get('vetting', { params })
      .then((response) => {
        commit(SET_VETTING_REQUESTS, response.data.data)
        commit(SET_VETTING_PAGINATION, response.data.meta)

        return response
      })
  },

  /**
   * Get the next set of paginated vetting booking requests.
   *
   * @return Promise
   */
  getNextVettingBookingRequests ({ commit, state, getters }, params = {}) {
    if (getters.haveNoMoreVettingBookingRequests) {
      return Promise.reject(new Error('There are no more vetting booking requests to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`vetting`, { params })
      .then((response) => {
        commit(ADD_VETTING_REQUESTS, response.data.data)
        commit(SET_VETTING_PAGINATION, response.data.meta)

        return response
      })
  },

  /**
   * Update a vetting booking requests details.
   *
   * @return Promise
   */
  updateVettingBookingRequest ({ commit }, { id, payload }) {
    return http.put(`vetting/${id}`, payload)
      .then(response => {
        commit(SET_VETTING_REQUEST, response.data)
      })
  },

  /**
   * Approve a vetting booking request as an admin.
   *
   * @return Promise
   */
  approveVettingBookingRequest (store, { id }) {
    return http.put(`vetting/${id}/approve`)
  },

  /**
   * Decline a vetting booking request as an admin.
   *
   * @return Promise
   */
  declineVettingBookingRequest ({ commit }, { id, reason }) {
    return http.put(`vetting/${id}/decline`, { reason: reason })
  },

  /**
   * Approves a draft booking request as an admin.
   * (Converts to Vetting Request)
   *
   * @return Promise
   */
  approveDraftBookingRequest ({ commit }, { id }) {
    return http.put(`/draft/${id}/approve`)
  },

  /**
   * Archive a draft booking request.
   *
   * @return Promise
   */
  archiveDraftBookingRequest ({ commit }, { id }) {
    return http.put(`/draft/${id}/archive`).then(response => {
      commit(SET_VETTING_REQUEST, response.data)
    })
  },

  /**
   * Restores a archived draft booking request.
   *
   * @return Promise
   */
  restoreDraftBookingRequest ({ commit }, { id }) {
    return http.put(`/draft/${id}/restore`)
  },

  /**
   * Delete a shortlisted talent from the booking request.
   *
   * @return Promise
   */
  deleteVettingBookingRequestShortlistedTalent ({ commit }, { id, talentId }) {
    return http.delete(`vetting/${id}/remove_shortlisted_talent/${talentId}`)
  },

  /**
   * Remove a vetting booking requests shortlisted talent.
   */
  removeVettingBookingRequestShortlistedTalent ({ commit }, id) {
    commit(REMOVE_VETTING_REQUEST_TALENT, id)
  },

  /**
   * Set the booking request.
   */
  setVettingBookingRequest ({ commit }, booking) {
    commit(SET_VETTING_REQUEST, booking)
  },

  setVettingRequestProperty ({ commit }, { property, value }) {
    commit(SET_VETTING_REQUEST_PROPERTY, { property, value })
  },

  resetVettingRequest ({ commit }) {
    commit(SET_VETTING_REQUEST, null)
  },

  setFilter ({ commit }, { filter, value }) {
    commit(SET_FILTER, { filter, value })
  },

  resetFilters ({ commit }) {
    commit(RESET_FILTERS)
  },

  getVettingCount ({ commit }) {
    return http.get(`vetting/count`)
      .then((response) => {
        commit(SET_VETTING_COUNT, response.data)

        return response
      })
  }
}

// Vetting booking request state mutations.
const mutations = {
  [SET_VETTING_REQUESTS] (state, requests) {
    state.requests = requests
  },

  [SET_VETTING_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [ADD_VETTING_REQUESTS] (state, requests) {
    state.requests = concat(state.requests, requests)
  },

  [SET_VETTING_REQUEST] (state, request) {
    state.request = request
  },

  [RESET_VETTING_REQUEST] (state) {
    state.request = null
  },

  [SET_VETTING_REQUEST_PROPERTY] (state, { property, value }) {
    set(state.request, property, value)
  },

  [REMOVE_VETTING_REQUEST_TALENT] (state, id) {
    let index = findIndex(state.request.talent, talent => talent.id === id)

    state.request.talent.splice(index, 1)
  },

  [SET_FILTER] (state, { filter, value }) {
    set(state.filters, filter, value)
  },

  [RESET_FILTERS] (state) {
    state.filters = initialFiltersState()
  },

  [SET_VETTING_COUNT] (state, vettingCount) {
    state.count = vettingCount
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
